import type {Content} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import type {SliceComponentProps} from '@prismicio/react'
import {PrismicRichText} from '@prismicio/react'
import {useLinkResolver} from 'prismicio'
import type {FC} from 'react'
import {ButtonV2} from 'src/common/ButtonV2'
import {Container} from 'src/common/Container'
import {getButtonsData} from 'src/common/helpers'
import type {Context} from 'src/common/types'

/**
 * Props for `ProductTourBanner`.
 */
export type ProductTourBannerProps = SliceComponentProps<Content.ProductTourBannerSlice> & {context: Context}

/**
 * Component for "ProductTourBanner" Slices.
 */
const ProductTourBanner: FC<ProductTourBannerProps> = ({slice, context, index: componentIndex}) => {
  const {primaryButtonData} = getButtonsData({
    primaryButton: slice.primary.primary_button,
    buttonsData: context.buttonsData,
  })

  const hasMobileImage = isFilled.imageThumbnail(slice.primary.asset.mobile)
  const {linkResolver} = useLinkResolver()

  return (
    <Container
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="!py-10 max-[900px]:!hidden md:!py-20"
    >
      <div className="mx-auto flex max-w-[1200px] flex-col gap-8 px-5 md:gap-12 md:px-10 lg:px-5 xl:px-0">
        <div className="flex flex-col gap-6">
          {isFilled.richText(slice.primary.tag) && (
            <PrismicRichText
              field={slice.primary.tag}
              components={{
                paragraph: ({children}) => <p className="text-button-s text-moss-blue">{children}</p>,
              }}
            />
          )}
          {isFilled.richText(slice.primary.heading) && (
            <PrismicRichText
              field={slice.primary.heading}
              components={{
                heading2: ({children}) => (
                  <h2 className="md:text-h2 text-32 font-medium leading-10 text-moss-black md:leading-[normal]">
                    {children}
                  </h2>
                ),
                strong: ({children}) => <strong className="font-medium text-moss-black/60">{children}</strong>,
              }}
            />
          )}
          {primaryButtonData && (
            <ButtonV2
              className="w-fit uppercase"
              variant="primary"
              size="medium"
              trackingData={{
                location: `component-${componentIndex}-product-tour-banner`,
                dataType: 'primary-button',
                pageName: context.pageUid,
                mktgId: primaryButtonData?.marketing_id || '',
              }}
              href={linkResolver(primaryButtonData?.link, 'primary-button-product-tour-banner')}
              newTrackingProps={{
                interaction: {
                  element: primaryButtonData?.marketing_id || 'primary-button-product-tour-banner',
                  elementText: primaryButtonData?.label || '',
                  location: `component-${componentIndex}-product-tour-banner`,
                },
                pageType: context.pageUid,
              }}
            >
              {primaryButtonData?.label}
            </ButtonV2>
          )}
        </div>
        <div className="rounded-lg border border-moss-black/10">
          {isFilled.image(slice.primary.asset) && hasMobileImage && (
            <>
              <PrismicNextImage field={slice.primary.asset.mobile} className="h-auto w-full rounded-t-lg md:hidden" />
              <PrismicNextImage field={slice.primary.asset} className="h-auto w-full rounded-lg max-md:hidden" />
            </>
          )}
        </div>
      </div>
    </Container>
  )
}

export default ProductTourBanner
